import React, { useEffect, useRef, useState } from 'react';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import { getItem } from 'helpers/storage';

interface RemoteApi {
    mount: (
        element: HTMLElement,
        options?: { basename?: string; token?: string | null }
    ) => void;
    unmount: (element: HTMLElement) => void;
}

interface MicroAppEmbedProps {
    bundleUrl: string;
    globalName: string;
    basename: string;
}

const loadPromises = new Map<string, Promise<RemoteApi>>();

function isEsmDevEntry(bundleUrl: string): boolean {
    return /\.tsx?(?:[?#].*)?$/i.test(bundleUrl);
}

function isLocalHostUrl(bundleUrl: string): boolean {
    try {
        const { hostname } = new URL(bundleUrl);
        return hostname === 'localhost' || hostname === '127.0.0.1';
    } catch {
        return /localhost|127\.0\.0\.1/i.test(bundleUrl);
    }
}

function toLocalHmrEntryUrl(bundleUrl: string): string {
    return new URL('src/embed-entry.tsx', bundleUrl).href;
}

async function loadEsmEntry(entryUrl: string): Promise<RemoteApi> {
    const mod = await import(/* @vite-ignore */ entryUrl);
    if (typeof mod.mount !== 'function' || typeof mod.unmount !== 'function') {
        throw new TypeError(`"${entryUrl}" must export mount and unmount`);
    }
    return { mount: mod.mount, unmount: mod.unmount };
}

async function loadIifeScript(
    bundleUrl: string,
    globalName: string,
    cacheKey: string
): Promise<RemoteApi> {
    const win = window as unknown as Window & Record<string, RemoteApi | undefined>;
    const script = document.createElement('script');
    script.src = bundleUrl;
    script.async = true;

    await new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => {
            script.remove();
            loadPromises.delete(cacheKey);
            reject(new Error(`Failed to load embed bundle from ${bundleUrl}`));
        };
        document.head.appendChild(script);
    });

    const api = win[globalName];
    if (!api) {
        script.remove();
        loadPromises.delete(cacheKey);
        throw new Error(
            `"${globalName}" global not found after loading ${bundleUrl}`
        );
    }
    return api;
}

async function loadRemote(
    bundleUrl: string,
    globalName: string
): Promise<RemoteApi> {
    const win = window as unknown as Window & Record<string, RemoteApi | undefined>;
    if (win[globalName]) {
        return win[globalName];
    }

    const cacheKey = `${globalName}:${bundleUrl}`;
    const existing = loadPromises.get(cacheKey);
    if (existing) {
        return existing;
    }

    const promise = (async () => {
        if (isEsmDevEntry(bundleUrl)) {
            return loadEsmEntry(bundleUrl);
        }

        // Local: try IIFE (yarn preview:embed), then HMR entry (yarn dev).
        if (isLocalHostUrl(bundleUrl)) {
            try {
                return await loadIifeScript(bundleUrl, globalName, cacheKey);
            } catch {
                return loadEsmEntry(toLocalHmrEntryUrl(bundleUrl));
            }
        }

        return loadIifeScript(bundleUrl, globalName, cacheKey);
    })();

    loadPromises.set(cacheKey, promise);
    return promise;
}

export function MicroAppEmbed({
    bundleUrl,
    globalName,
    basename,
}: Readonly<MicroAppEmbedProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isActive = true;
        const element = containerRef.current;
        let api: RemoteApi | undefined;

        loadRemote(bundleUrl, globalName)
            .then((remote) => {
                if (!isActive || !element) {
                    return;
                }
                api = remote;
                const userData = getItem('user');
                remote.mount(element, {
                    basename,
                    token: userData?.token ?? null,
                });
                setIsLoading(false);
            })
            .catch((err: Error) => {
                if (isActive) {
                    setError(err.message);
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
            if (api && element) {
                api.unmount(element);
            }
        };
    }, [basename, bundleUrl, globalName]);

    if (error) {
        return (
            <div style={{ padding: 24, color: '#b00020' }}>
                Failed to load microfrontend. {error}
            </div>
        );
    }

    return (
        <div>
            {isLoading && <LoadingContent layout="absolute" />}
            <div ref={containerRef} />
        </div>
    );
}
